import React, { useState, useEffect } from 'react';
import { KnowledgeBase, KnowledgeBaseFilter } from '@shared/types';
import { getDifficultyDisplayName } from '@shared/utils';

interface KnowledgeBaseListProps {
  knowledgeBases: KnowledgeBase[];
  onEdit: (knowledgeBase: KnowledgeBase) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
  onViewDocuments?: (knowledgeBase: KnowledgeBase) => void;
  loading?: boolean;
}

export const KnowledgeBaseList: React.FC<KnowledgeBaseListProps> = ({
  knowledgeBases,
  onEdit,
  onDelete,
  onView,
  onViewDocuments,
  loading = false
}) => {
  const [filter, setFilter] = useState<KnowledgeBaseFilter>({});
  const [filteredBases, setFilteredBases] = useState<KnowledgeBase[]>(knowledgeBases);

  useEffect(() => {
    let filtered = [...knowledgeBases];

    if (filter.category) {
      filtered = filtered.filter(base => base.category === filter.category);
    }

    if (filter.subject) {
      filtered = filtered.filter(base => base.subject === filter.subject);
    }

    if (filter.difficulty_level) {
      filtered = filtered.filter(base => base.difficulty_level === filter.difficulty_level);
    }

    if (filter.status) {
      filtered = filtered.filter(base => base.status === filter.status);
    }

    if (filter.search) {
      const searchTerm = filter.search.toLowerCase();
      filtered = filtered.filter(base =>
        base.name.toLowerCase().includes(searchTerm) ||
        (base.description && base.description.toLowerCase().includes(searchTerm))
      );
    }

    setFilteredBases(filtered);
  }, [knowledgeBases, filter]);

  const handleFilterChange = (key: keyof KnowledgeBaseFilter, value: string) => {
    setFilter((prev: KnowledgeBaseFilter) => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-blue-100 text-blue-800';
      case 'intermediate': return 'bg-orange-100 text-orange-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">加载中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 筛选器 */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              搜索
            </label>
            <input
              type="text"
              placeholder="搜索知识库名称或描述..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              分类
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.category || ''}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">全部分类</option>
              <option value="数学">数学</option>
              <option value="计算机科学">计算机科学</option>
              <option value="物理">物理</option>
              <option value="化学">化学</option>
              <option value="生物">生物</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              科目
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.subject || ''}
              onChange={(e) => handleFilterChange('subject', e.target.value)}
            >
              <option value="">全部科目</option>
              <option value="基础数学">基础数学</option>
              <option value="编程">编程</option>
              <option value="算法">算法</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              难度
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.difficulty_level || ''}
              onChange={(e) => handleFilterChange('difficulty_level', e.target.value)}
            >
              <option value="">全部难度</option>
              <option value="beginner">初级</option>
              <option value="intermediate">中级</option>
              <option value="advanced">高级</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              状态
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">全部状态</option>
              <option value="active">活跃</option>
              <option value="archived">已归档</option>
              <option value="draft">草稿</option>
            </select>
          </div>
        </div>
      </div>

      {/* 结果统计 */}
      <div className="text-sm text-gray-600">
        共找到 {filteredBases.length} 个知识库
      </div>

      {/* 知识库列表 */}
      {filteredBases.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          没有找到匹配的知识库
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBases.map((knowledgeBase) => (
            <div
              key={knowledgeBase.id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onView(knowledgeBase.id)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                    {knowledgeBase.name}
                  </h3>
                  <div className="flex space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(knowledgeBase.status)}`}>
                      {knowledgeBase.status === 'active' ? '活跃' :
                        knowledgeBase.status === 'archived' ? '已归档' : '草稿'}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(knowledgeBase.difficulty_level)}`}>
                      {getDifficultyDisplayName(knowledgeBase.difficulty_level)}
                    </span>
                  </div>
                </div>

                {knowledgeBase.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {knowledgeBase.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="font-medium">分类:</span>
                    <span className="ml-1">{knowledgeBase.category}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="font-medium">科目:</span>
                    <span className="ml-1">{knowledgeBase.subject}</span>
                  </div>
                  {knowledgeBase.target_audience && (
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="font-medium">目标用户:</span>
                      <span className="ml-1">{knowledgeBase.target_audience}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>版本 {knowledgeBase.version}</span>
                  <span>创建于 {new Date(knowledgeBase.created_at).toLocaleDateString('zh-CN')}</span>
                </div>

                <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(knowledgeBase.id);
                    }}
                    className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    查看
                  </button>
                  {onViewDocuments && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDocuments(knowledgeBase);
                      }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
                    >
                      文档
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(knowledgeBase);
                    }}
                    className="flex-1 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                  >
                    编辑
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(knowledgeBase.id);
                    }}
                    className="flex-1 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
